import React from 'react';
import { RouteComponentProps, Route as BaseRoute } from 'react-router-dom';
import loadable from '@loadable/component';

import { readPathId } from 'modules/shared/utils';
import { Switch, NotifiedRoute as Route, NotFound } from 'modules/shared/components';
import { IdRouteParams } from 'modules/shared/types';
import { DefaultLayout } from 'modules/shared/layouts';
import { buildModuleRoutes } from 'modules/shared/utils/route';

import { ModuleSelector } from './components';
import { RouterProps } from './router-type';

export const Router: React.FC<RouterProps> = ({ nodes, rootNavigationElement }: RouterProps) => {
    return (
        <Switch>
            {buildModuleRoutes(ModuleSelector, nodes || [], rootNavigationElement)}
            <Route
                exact
                path="/aide"
                render={() => {
                    const HelpPage = loadable(() =>
                        import(/* webpackChunkName: "help-page" */ '../../pages/help/help-page'),
                    );
                    return (
                        <DefaultLayout>
                            <HelpPage filterParams={[{ key: 'type', value: 'aide' }]} />
                        </DefaultLayout>
                    );
                }}
            />
            <Route
                exact
                path="/reglement"
                render={() => {
                    const HelpPage = loadable(() =>
                        import(/* webpackChunkName: "help-page" */ '../../pages/help/help-page'),
                    );
                    return (
                        <DefaultLayout>
                            <HelpPage filterParams={[{ key: 'type', value: 'reglement' }]} />
                        </DefaultLayout>
                    );
                }}
            />
            <Route
                exact
                path="/questions-frequentes"
                render={() => {
                    const HelpPage = loadable(() =>
                        import(/* webpackChunkName: "help-page" */ '../../pages/help/help-page'),
                    );
                    return (
                        <DefaultLayout>
                            <HelpPage filterParams={[{ key: 'type', value: 'faq' }]} />
                        </DefaultLayout>
                    );
                }}
            />
            <Route
                exact
                path="/contact"
                render={() => {
                    const ContactPage = loadable(() =>
                        import(/* webpackChunkName: "contact-page" */ '../../pages/contact/contact-page'),
                    );
                    return (
                        <DefaultLayout>
                            <ContactPage />
                        </DefaultLayout>
                    );
                }}
            />
            <Route
                exact
                path="/mention-legale"
                render={() => {
                    const LegalMentionPage = loadable(() =>
                        import(
                            /* webpackChunkName: "legal-mention-page" */ '../../pages/legal-mention/legal-mention-page'
                        ),
                    );
                    return (
                        <DefaultLayout>
                            <LegalMentionPage />
                        </DefaultLayout>
                    );
                }}
            />
            <Route
                exact
                path="/centre-de-preferences"
                render={() => {
                    const CookiePreferencesPage = loadable(() =>
                        import(
                            /* webpackChunkName: "cookie-preferences-page" */ '../../pages/cookie-preferences/cookie-preferences-page'
                        ),
                    );
                    return (
                        <DefaultLayout>
                            <CookiePreferencesPage />
                        </DefaultLayout>
                    );
                }}
            />
            <Route
                exact
                path="/mot-de-passe-oublie"
                render={() => {
                    const ForgottenPasswordPage = loadable(() =>
                        import(
                            /* webpackChunkName: "forgotten-password-page" */ '../../pages/forgotten-password/forgotten-password-page'
                        ),
                    );
                    return (
                        <DefaultLayout>
                            <ForgottenPasswordPage />
                        </DefaultLayout>
                    );
                }}
            />
            <Route
                exact
                path="/reinit-mot-de-passe"
                render={() => {
                    const ResetPasswordPage = loadable(() =>
                        import(
                            /* webpackChunkName: "reset-password-page" */ '../../pages/reset-password/reset-password-page'
                        ),
                    );
                    return (
                        <DefaultLayout>
                            <ResetPasswordPage />
                        </DefaultLayout>
                    );
                }}
            />
            <Route
                exact
                path="/accepter-transfert"
                render={() => {
                    const AcceptTransferPage = loadable(() =>
                        import(
                            /* webpackChunkName: "accept-transfer-page" */ '../../pages/accept-transfer/accept-transfer-page'
                        ),
                    );
                    return (
                        <DefaultLayout>
                            <AcceptTransferPage />
                        </DefaultLayout>
                    );
                }}
            />
            <Route
                exact
                path="/confirmation-transfert"
                render={() => {
                    const TransferConfirmationPage = loadable(() =>
                        import(
                            /* webpackChunkName: "transfer-confirmation-page" */ '../../pages/transfer-confirmation/transfer-confirmation-page'
                        ),
                    );
                    return (
                        <DefaultLayout>
                            <TransferConfirmationPage />
                        </DefaultLayout>
                    );
                }}
            />
            <Route
                exact
                path="/newsletters"
                render={() => {
                    const NewsletterPage = loadable(() =>
                        import(
                            /* webpackChunkName: "newsletter-subscribe-page" */ '../../pages/newsletter-subscribe/newsletter-page'
                        ),
                    );
                    return (
                        <DefaultLayout>
                            <NewsletterPage />
                        </DefaultLayout>
                    );
                }}
            />
            <Route
                exact
                path="/gestion-abonnements"
                render={() => {
                    const ManageNewsletterPage = loadable(() =>
                        import(
                            /* webpackChunkName: "manage-newsletter-page" */ '../../pages/newsletter-manage/manage-newsletter-page'
                        ),
                    );
                    return (
                        <DefaultLayout>
                            <ManageNewsletterPage />
                        </DefaultLayout>
                    );
                }}
            />
            <Route
                exact
                path="/confirmation-parrainage"
                render={() => {
                    const SponsorshipValidationPage = loadable(() =>
                        import(
                            /* webpackChunkName: "sponsorship-validation-page" */ '../../pages/sponsorship-validation/sponsorship-validation-page'
                        ),
                    );
                    return (
                        <DefaultLayout>
                            <SponsorshipValidationPage />
                        </DefaultLayout>
                    );
                }}
            />
            <Route
                exact
                path="/magasin/:id"
                render={(props: RouteComponentProps<IdRouteParams>) => {
                    const id = readPathId(props.match.params.id);
                    const StorePage = loadable(() =>
                        import(/* webpackChunkName: "store-page" */ '../../pages/store/store-page'),
                    );
                    return (
                        <DefaultLayout>
                            <StorePage id={id} filterParams={[{ key: 'id', value: id }]} />
                        </DefaultLayout>
                    );
                }}
            />
            <Route
                exact
                path="/recherche/:criteria"
                render={(props: RouteComponentProps<{ criteria: string }>) => {
                    const { criteria } = props.match.params;
                    const SearchModule = loadable(() =>
                        import(/* webpackChunkName: "search" */ '../../../search/search'),
                    );

                    return <SearchModule criteria={criteria} />;
                }}
            />
            <BaseRoute
                render={() => {
                    return (
                        <DefaultLayout>
                            <NotFound />
                        </DefaultLayout>
                    );
                }}
            />
        </Switch>
    );
};
